// 回放日曆（PLAY-01）：取代原生 date 欄位——月曆標出有錄影的日子（雲端 façade recording-days ∪ 本地 pb.days）。
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addMonths, monthCells } from './calendar';

interface Props {
  value: string; // YYYY-MM-DD
  max: string;
  /** 月曆目前顯示的月份（有錄影的日子依這個月去問）。 */
  month: string;
  onMonth(month: string): void;
  /** 顯示中的月份有錄影的日子；undefined = 還在查。 */
  marked: Set<string> | undefined;
  onPick(day: string): void;
}

export function DayPicker({ value, max, month, onMonth, marked, onPick }: Props) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const [y, m] = month.split('-').map(Number);
  const title = new Date(Date.UTC(y, m - 1, 15)).toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', timeZone: 'UTC' });
  // 週一起的星期標題（2026-06-01 是週一）
  const week = [...Array(7).keys()].map((i) => new Date(Date.UTC(2026, 5, 1 + i)).toLocaleDateString(i18n.language, { weekday: 'narrow', timeZone: 'UTC' }));
  return (
    <div className="day-picker" ref={ref}>
      <button
        className="btn"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={t('playback.day')}
        onClick={() => {
          onMonth(value.slice(0, 7));
          setOpen((o) => !o);
        }}
      >
        📅 {value}
      </button>
      {open && (
        <div className="day-pop card" role="dialog" aria-label={t('playback.day')}>
          <div className="toolbar">
            <button className="icon-btn" aria-label={t('playback.prev_month')} onClick={() => onMonth(addMonths(month, -1))}>
              ◀
            </button>
            <strong className="spacer" style={{ textAlign: 'center' }}>
              {title}
            </strong>
            <button
              className="icon-btn"
              aria-label={t('playback.next_month')}
              disabled={addMonths(month, 1) > max.slice(0, 7)}
              onClick={() => onMonth(addMonths(month, 1))}
            >
              ▶
            </button>
          </div>
          <div className="day-grid">
            {week.map((w, i) => (
              <span key={`w${i}`} className="muted small">
                {w}
              </span>
            ))}
            {monthCells(month).map((d, i) =>
              d ? (
                <button
                  key={d}
                  data-day={d}
                  className={`day${d === value ? ' sel' : ''}${marked?.has(d) ? ' has-rec' : ''}`}
                  disabled={d > max}
                  aria-pressed={d === value}
                  aria-label={marked?.has(d) ? t('playback.day_has_rec', { day: d }) : d}
                  title={marked && !marked.has(d) && d <= max ? t('playback.day_no_rec') : undefined}
                  onClick={() => {
                    onPick(d);
                    setOpen(false);
                  }}
                >
                  {Number(d.slice(8))}
                </button>
              ) : (
                <span key={`p${i}`} />
              ),
            )}
          </div>
          <p className="muted small">{marked === undefined ? t('common.loading') : t('playback.day_legend')}</p>
        </div>
      )}
    </div>
  );
}
